import { computed, makeObservable, observable } from 'mobx';

import {
  BRANDING_MAX_ENCODED_BYTES,
  computeDrawPlan,
  decodeImageFile,
  drawBrandingImage,
  type BrandingImageFit,
  type DecodedImage,
  type DrawPlan,
} from 'helpers/brandingImage';
import { toError } from 'helpers/toError';

/**
 * The rasterising half of the pipeline, injected so the model can be tested.
 *
 * Under vitest there is no `canvas` package, so `getContext('2d')` yields
 * nothing and the real `draw` cannot run at all. Every branch that matters —
 * the fit flip, the byte cap, the reject paths, the empty state — is reachable
 * with a fake.
 */
export interface BrandingImageDeps {
  readonly decode: (file: File) => Promise<DecodedImage>;
  readonly draw: (source: DecodedImage, plan: DrawPlan) => string;
}

const BROWSER_DEPS: BrandingImageDeps = {
  decode: decodeImageFile,
  draw: (source, plan) => drawBrandingImage(source, plan),
};

/** What the editor currently has to show, and whether it can be saved. */
export type RasterisedImage =
  { readonly kind: 'none' } | { readonly kind: 'ready'; readonly dataUrl: string } | { readonly kind: 'failed'; readonly message: string };

/**
 * MobX-backed behaviour for the Branding config editor: owns the selected
 * file, the fit mode, and the square derived from them.
 *
 * Exactly one step of the pipeline is asynchronous — decoding the file — and it
 * happens once per selection. Everything downstream of a decoded image is
 * synchronous (`drawImage` and `toDataURL` both are), which is why the square,
 * its size and its validity are plain `computed`s rather than a query: flipping
 * the fit re-derives without re-decoding.
 *
 * A `MobxQuery` would be the wrong tool here. Its job is a value re-fetched
 * when observable params change, and the epoch guard that drops superseded
 * answers. This model's async step is not params-driven, so there is no
 * reaction to arm. It does still need last-write-wins across two rapid
 * selections, which `_selectionEpoch` provides in three lines.
 */
export class BrandingImageModel {
  private readonly _deps: BrandingImageDeps;

  private _decoded: DecodedImage | null = null;
  private _decoding = false;
  private _error: string | null = null;
  private _fit: BrandingImageFit = 'contain';
  /**
   * The image already persisted to config, shown until a new file is picked.
   * Cleared by {@link clear} so removing an image is distinguishable from
   * never having had one.
   */
  private _savedImage: string | null;
  /**
   * Guard token for {@link selectFile}, deliberately NOT observable: it takes
   * part in no derivation, and making it observable would invalidate every
   * computed on each selection for nothing.
   */
  private _selectionEpoch = 0;

  constructor(savedImage: string | null, deps: BrandingImageDeps = BROWSER_DEPS) {
    this._savedImage = savedImage;
    this._deps = deps;
    makeObservable<BrandingImageModel, '_decoded' | '_decoding' | '_error' | '_fit' | '_savedImage'>(this, {
      // observable.ref: a decoded HTMLImageElement must never be walked by
      // MobX's deep proxy.
      _decoded: observable.ref,
      _decoding: observable,
      _error: observable,
      _fit: observable,
      _savedImage: observable,
      byteLength: computed,
      rasterised: computed,
      tooLarge: computed,
    });
  }

  /**
   * Encoded size of the square. Base64 is ASCII, so the string length is the
   * byte count, and it is the encoded form that `GET /config` has to carry.
   */
  get byteLength(): number {
    const rasterised = this.rasterised;
    return rasterised.kind === 'ready' ? rasterised.dataUrl.length : 0;
  }

  /**
   * Whether the fit toggle can do anything.
   *
   * Only a freshly decoded file is re-drawn; an image already saved to config
   * is stored square with its fit baked in and cannot be re-fitted without the
   * original. Offering an inert control would be worse than hiding it.
   */
  get canChooseFit(): boolean {
    return this._decoded !== null;
  }

  /** The square to preview, or null when there is nothing to show. */
  get dataUrl(): string | null {
    const rasterised = this.rasterised;
    return rasterised.kind === 'ready' ? rasterised.dataUrl : null;
  }

  get errorMessage(): string | null {
    if (this._error !== null) return this._error;
    const rasterised = this.rasterised;
    if (rasterised.kind === 'failed') return rasterised.message;
    if (this.tooLarge) {
      return `The image encodes to ${Math.round(this.byteLength / 1024)} KB, over the ${Math.round(BRANDING_MAX_ENCODED_BYTES / 1024)} KB limit. Use a simpler image.`;
    }
    return null;
  }

  get fit(): BrandingImageFit {
    return this._fit;
  }

  get isDecoding(): boolean {
    return this._decoding;
  }

  /** False while anything would stop this being saved — gates the modal's Save. */
  get isValid(): boolean {
    return this.errorMessage === null;
  }

  /**
   * The square, derived from whichever of a freshly decoded file and the
   * already-saved image is current.
   *
   * Returns a failure rather than throwing: this is a `computed` read during
   * render, and an exception here would take the modal down instead of showing
   * the user what is wrong with their file.
   */
  get rasterised(): RasterisedImage {
    if (this._decoded === null) {
      return this._savedImage === null ? { kind: 'none' } : { kind: 'ready', dataUrl: this._savedImage };
    }
    try {
      return { kind: 'ready', dataUrl: this._deps.draw(this._decoded, computeDrawPlan(this._decoded.size, this._fit)) };
    } catch (err) {
      return { kind: 'failed', message: toError(err).message };
    }
  }

  /** The `branding` config value this editor contributes. */
  get saveValue(): { readonly image: string | null } {
    return { image: this.isValid ? this.dataUrl : this._savedImage };
  }

  /** True once the encoded square is too large for a config row to carry. */
  get tooLarge(): boolean {
    return this.byteLength > BRANDING_MAX_ENCODED_BYTES;
  }

  /** Drop the image entirely — distinct from never having selected one. */
  clear = (): void => {
    this._selectionEpoch += 1;
    this.releaseDecoded();
    this._savedImage = null;
    this._error = null;
  };

  /**
   * Release the last object URL. Called when the editor unmounts.
   *
   * Bumps the epoch as {@link clear} does, and that is load-bearing rather than
   * symmetry for its own sake: releasing `_decoded` alone leaks a decode that
   * is still in flight, because it lands afterwards, passes selectFile's epoch
   * check, assigns itself, and has nothing left to clean it up — dispose is not
   * called twice. Bumping first routes that late result into selectFile's own
   * discard-and-release branch.
   */
  dispose = (): void => {
    this._selectionEpoch += 1;
    this.releaseDecoded();
  };

  private releaseDecoded(): void {
    this._decoded?.release();
    this._decoded = null;
  }

  /**
   * Decode `file` and make it the current image.
   *
   * Last-write-wins: a slow decode that resolves after a later selection is
   * discarded (and its object URL released) rather than clobbering the newer
   * image.
   */
  selectFile = async (file: File): Promise<void> => {
    const epoch = (this._selectionEpoch += 1);
    this._decoding = true;
    this._error = null;

    try {
      const decoded = await this._deps.decode(file);
      if (epoch !== this._selectionEpoch) {
        decoded.release();
        return;
      }
      this.releaseDecoded();
      this._decoded = decoded;
    } catch (err) {
      if (epoch !== this._selectionEpoch) return;
      this._error = toError(err).message;
    } finally {
      if (epoch === this._selectionEpoch) this._decoding = false;
    }
  };

  setFit = (fit: BrandingImageFit): void => {
    this._fit = fit;
  };
}
